'use client'

import Image from "next/image"
import { ClockIcon, CurrencyDollarIcon } from "@heroicons/react/24/outline"
import { useDemoContext } from './DemoContext'

// Feature data structure
interface FeatureItem {
    key: string
    label: string
    title: string
    icon: any
    description: string
    eta?: string
    pricing?: string
    demoUrl?: string
}

interface FeatureGroup {
    group: string
    items: FeatureItem[]
}

interface InteractiveControlsProps {
    features: FeatureGroup[]
}

export function InteractiveControls({ features }: InteractiveControlsProps) {
    const { openDemo } = useDemoContext()

    const handleCardClick = (demoUrl: string, title: string) => {
        openDemo(demoUrl, title)
    }

    return (
        <div className="not-prose mt-6 grid gap-4 md:grid-cols-2">
            {features.map(({ group, items }) => (
                <section key={group} className={items.length === 1 ? "md:col-span-1" : "md:col-span-2"}>
                    <h3 className="mt-0 mb-3 text-base font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                        {group}
                    </h3>
                    <div className={`grid grid-cols-1 gap-6 ${items.length > 1 ? "md:grid-cols-2" : ""}`}>
                        {items.map(({ key, label, title, icon, description, eta, pricing, demoUrl }) => (
                            <div
                                key={key}
                                className={`flex w-full items-start rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-zinc-900 ${
                                    demoUrl ? 'cursor-pointer hover:border-blue-300 hover:shadow-md transition-all duration-200' : ''
                                }`}
                                onClick={demoUrl ? () => handleCardClick(demoUrl, title) : undefined}
                            >
                                <div className="flex w-32 flex-col items-center">
                                    <Image src={icon} alt={label} width={80} height={80} />
                                    <div className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-200">
                                        {label}
                                    </div>
                                    {demoUrl && (
                                        <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                                            Click for demo
                                        </div>
                                    )}
                                </div>
                                <div className="ml-4 flex-1">
                                    <h4 className="m-0 text-lg font-semibold dark:text-gray-100">{title}</h4>
                                    <p className="mt-1 mb-2 text-sm text-gray-700 dark:text-gray-300">
                                        {description}
                                    </p>
                                    <div className="flex flex-wrap gap-4 text-sm">
                                        {eta && (
                                            <div className="flex items-center text-gray-700 dark:text-gray-300">
                                                <ClockIcon className="mr-1 h-4 w-4" aria-hidden="true" /> {eta}
                                            </div>
                                        )}
                                        {pricing && (
                                            <div className="flex items-center text-gray-700 dark:text-gray-300">
                                                <CurrencyDollarIcon className="mr-1 h-4 w-4" aria-hidden="true" />{" "}
                                                {pricing}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}